import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import AppShell from './components/AppShell';
import { ROLES } from './mock';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import DashboardPage from './pages/DashboardPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import SalesHeadDashboardPage from './pages/SalesHeadDashboardPage';
import TeamPage from './pages/TeamPage';
import ClientsPage from './pages/ClientsPage';
import NewClientPage from './pages/NewClientPage';
import ClientProfilePage from './pages/ClientProfilePage';
import MRIWizardPage from './pages/MRIWizardPage';
import ReportPreviewPage from './pages/ReportPreviewPage';
import SettingsPage from './pages/SettingsPage';
import AlertsPage from './pages/AlertsPage';
import UsersAdminPage from './pages/UsersAdminPage';
import PrefillPage from './pages/PrefillPage';
import { Toaster } from './components/ui/toaster';

const homeForRole = (role) => role === ROLES.ADMIN ? '/admin' : role === ROLES.SALES_HEAD ? '/sales' : '/dashboard';

const Protected = ({ children, allow }) => {
  const { user, ready } = useApp();
  const location = useLocation();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to={homeForRole(user.role)} replace />;
  return <AppShell>{children}</AppShell>;
};

const RootRedirect = () => {
  const { user, ready } = useApp();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeForRole(user.role)} replace />;
};

// Handle #session_id fragment on any route (may land on '/', '/dashboard' etc.)
const RouteBoundary = () => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallbackPage />;
  return null;
};

function AppInner() {
  return (
    <BrowserRouter>
      <BootstrapCallback />
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/prefill/:sid" element={<PrefillPage />} />

        <Route path="/admin" element={<Protected allow={[ROLES.ADMIN]}><AdminDashboardPage /></Protected>} />
        <Route path="/admin/team" element={<Protected allow={[ROLES.ADMIN]}><TeamPage mode="admin" /></Protected>} />
        <Route path="/admin/users" element={<Protected allow={[ROLES.ADMIN]}><UsersAdminPage /></Protected>} />

        <Route path="/sales" element={<Protected allow={[ROLES.SALES_HEAD]}><SalesHeadDashboardPage /></Protected>} />
        <Route path="/sales/rms" element={<Protected allow={[ROLES.SALES_HEAD]}><TeamPage mode="sales" /></Protected>} />

        <Route path="/dashboard" element={<Protected allow={[ROLES.RM]}><DashboardPage /></Protected>} />
        <Route path="/clients/new" element={<Protected allow={[ROLES.RM]}><NewClientPage /></Protected>} />

        <Route path="/clients" element={<Protected><ClientsPage /></Protected>} />
        <Route path="/clients/:id" element={<Protected><ClientProfilePage /></Protected>} />
        <Route path="/clients/:clientId/mri/new" element={<Protected allow={[ROLES.RM]}><MRIWizardPage /></Protected>} />
        <Route path="/clients/:clientId/mri/:sessionId" element={<Protected><MRIWizardPage /></Protected>} />
        <Route path="/clients/:clientId/mri/:sessionId/report" element={<Protected><ReportPreviewPage /></Protected>} />

        <Route path="/alerts" element={<Protected allow={[ROLES.ADMIN, ROLES.SALES_HEAD]}><AlertsPage /></Protected>} />
        <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />

        <Route path="*" element={<RootRedirect />} />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}

// Detects #session_id even if the OAuth redirect lands on unexpected route
const BootstrapCallback = () => {
  const location = useLocation();
  if (location.hash?.includes('session_id=') && !location.pathname.startsWith('/auth/callback')) {
    return <AuthCallbackPage />;
  }
  return null;
};

function App() {
  return (
    <div className="App">
      <AppProvider>
        <AppInner />
      </AppProvider>
    </div>
  );
}

export default App;
